import { useState, useRef } from 'react';
import { searchChristianSongs, searchSongsByLyrics } from '../services/songApi';
import { aiSongSearch, isGroqAvailable } from '../services/groqAi';
import analyticsService from '../services/analytics';
import { toast } from '../lib/toast';

// The "AI song finder": say the title, or sing a few lines, and SleekHear AI
// (falling back to the online song database) suggests matching songs.
export default function useAiSongFinder({ addDebugLog, logActivity }) {
  const [groqEnabled] = useState(isGroqAvailable());
  const [aiSongListening, setAiSongListening] = useState(false);
  const [aiSongTranscript, setAiSongTranscript] = useState('');
  const [aiSongResults, setAiSongResults] = useState([]);
  const [aiSongSearching, setAiSongSearching] = useState(false);
  const [aiSongMode, setAiSongMode] = useState('speak'); // 'speak' | 'sing'
  const [aiSingLyrics, setAiSingLyrics] = useState('');

  const aiSongRecognitionRef = useRef(null);
  const aiSingRecognitionRef = useRef(null);

  const searchSongOnline = async (query) => {
    setAiSongSearching(true);
    addDebugLog(`🔍 AI Song: Searching for "${query}"...`);

    try {
      let results = [];

      // Try SleekHear AI first for intelligent song matching
      if (groqEnabled) {
        addDebugLog(`🤖 Using SleekHear AI for song search...`);
        try {
          results = await aiSongSearch(query, 'speak');
          if (results.length > 0) {
            addDebugLog(`✅ SleekHear AI: Found ${results.length} song(s)`);
          }
        } catch (groqError) {
          addDebugLog(`⚠️ SleekHear AI failed, using fallback: ${groqError.message}`);
        }
      }

      // Fallback to local database search
      if (results.length === 0) {
        results = await searchChristianSongs(query);
      }

      setAiSongResults(results);

      if (results.length > 0) {
        addDebugLog(`✅ AI Song: Found ${results.length} song(s)`);
      } else {
        addDebugLog(`⚠️ AI Song: No songs found for "${query}"`);
      }
    } catch (error) {
      addDebugLog(`❌ AI Song search failed: ${error.message}`);
    } finally {
      setAiSongSearching(false);
    }
  };

  const searchSongByLyrics = async (lyrics) => {
    setAiSongSearching(true);
    addDebugLog(`🔍 AI Song: Searching by lyrics...`);

    try {
      let results = [];

      // Try SleekHear AI first for intelligent lyrics matching
      if (groqEnabled) {
        addDebugLog(`🤖 Using SleekHear AI for lyrics matching...`);
        try {
          results = await aiSongSearch(lyrics, 'sing');
          if (results.length > 0) {
            addDebugLog(`✅ SleekHear AI: Matched ${results.length} song(s) from lyrics`);
          }
        } catch (groqError) {
          addDebugLog(`⚠️ SleekHear AI failed, using fallback: ${groqError.message}`);
        }
      }

      // Fallback to local database lyrics search
      if (results.length === 0) {
        results = await searchSongsByLyrics(lyrics);
      }

      setAiSongResults(results);

      if (results.length > 0) {
        addDebugLog(`✅ AI Song: Found ${results.length} song(s) matching lyrics`);
      } else {
        addDebugLog(`⚠️ AI Song: No songs matched those lyrics`);
      }
    } catch (error) {
      addDebugLog(`❌ AI Song lyrics search failed: ${error.message}`);
    } finally {
      setAiSongSearching(false);
    }
  };

  const toggleSingMode = () => {
    if (!aiSingRecognitionRef.current) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        toast.error('Speech recognition is not supported in this browser. Use Chrome or Edge.');
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      let lyricsBuffer = '';

      recognition.onstart = () => {
        setAiSongListening(true);
        setAiSingLyrics('');
        lyricsBuffer = '';
        addDebugLog('🎤 AI Song: Listening to you sing...');
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript + ' ';
          }
        }

        if (finalTranscript) {
          lyricsBuffer += finalTranscript;
          setAiSingLyrics(lyricsBuffer.trim());
          addDebugLog(`🎵 Captured lyrics: "${finalTranscript.trim()}"`);

          if (lyricsBuffer.split(' ').length >= 5) {
            searchSongByLyrics(lyricsBuffer.trim());
          }
        }
      };

      recognition.onerror = (event) => {
        addDebugLog(`❌ AI Sing error: ${event.error}`);
        setAiSongListening(false);
      };

      recognition.onend = () => {
        setAiSongListening(false);
      };

      aiSingRecognitionRef.current = recognition;
    }

    if (aiSongListening) {
      aiSingRecognitionRef.current.stop();
    } else {
      aiSingRecognitionRef.current.start();
      analyticsService.trackEvent('ai_song_search_used', {
        mode: 'sing_lyrics'
      });
    }
  };

  const toggleAiSongListening = () => {
    if (aiSongMode === 'speak') {
      if (!aiSongRecognitionRef.current) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
          toast.error('Speech recognition is not supported in this browser. Use Chrome or Edge.');
          return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
          setAiSongListening(true);
          addDebugLog('🎤 AI Song: Listening for song name...');
          logActivity('ai_song_search_started', {
            mode: aiSongMode
          });
        };

        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setAiSongTranscript(transcript);
          addDebugLog(`🎤 AI Song: Heard "${transcript}"`);
          searchSongOnline(transcript);
        };

        recognition.onerror = (event) => {
          addDebugLog(`❌ AI Song error: ${event.error}`);
          setAiSongListening(false);
        };

        recognition.onend = () => {
          setAiSongListening(false);
        };

        aiSongRecognitionRef.current = recognition;
      }

      if (aiSongListening) {
        aiSongRecognitionRef.current.stop();
      } else {
        aiSongRecognitionRef.current.start();
        analyticsService.trackEvent('ai_song_search_used', {
          mode: 'speak_title'
        });
      }
    } else {
      toggleSingMode();
    }
  };

  const clearAiSongResults = () => {
    setAiSongResults([]);
    setAiSongTranscript('');
    setAiSingLyrics('');
  };

  return {
    groqEnabled,
    aiSongListening,
    aiSongTranscript,
    aiSongResults,
    aiSongSearching,
    aiSongMode,
    setAiSongMode,
    aiSingLyrics,
    toggleAiSongListening,
    clearAiSongResults,
  };
}
